<template>
    <div data-testid="data-table" class="data-table-wrapper gap-sm flex flex-col">
        <!-- flex-wrap + order-last: below lg the search box takes its own full
             row under the controls, instead of being squeezed by the view
             toggle / column picker / sort (the variant-A trade-off recorded on
             ITDEVELOP-3170). -->
        <div class="gap-sm flex flex-wrap items-center justify-between">
            <!-- Leading slot so pages can put their filter controls on the same
                 row as the view toggle / column picker instead of stacking a
                 separate row above the table (ITDEVELOP-3170 QA). -->
            <slot name="table-header-filters" />

            <div v-if="enableSearch" class="input-wrapper order-last w-full pr-2.5 lg:order-none lg:max-w-96">
                <input v-model="searchQuery" type="search" :placeholder="searchPlaceholder" autocomplete="off" />
                <i class="i-search" />
            </div>

            <TableDisplayControls
                v-if="displayControlsActive"
                :view="effectiveView"
                :columns="toggleableColumns"
                @update:view="setView"
                @toggle-column="toggleColumn"
                @reset="resetViewPrefs"
            />

            <MobileTableSort
                v-if="enableSorting && !gridMode"
                :title="title || ''"
                :columns="
                    table.getFlatHeaders().map((header) => {
                        return {
                            key: header.id,
                            label:
                                typeof header.column.columnDef.header === 'string'
                                    ? header.column.columnDef.header
                                    : header.id,
                        };
                    })
                "
                :selected-key="selectedKey"
                @sort-table="setSorting"
                @clear-sorting="clearSorting"
            />
            <slot name="table-header-actions" />
        </div>

        <template v-if="gridMode">
            <slot
                name="custom-grid-mode"
                :data="table.getRowModel().rows.map((row) => row.original)"
                :handle-row-click="handleRowClick"
            ></slot>
        </template>

        <template v-else-if="cardsActive">
            <!-- A page's custom-grid-mode slot is its cards renderer: when the
                 user (or mobileMode) picks cards, prefer it over the generic
                 label/value cards. -->
            <slot
                v-if="slots['custom-grid-mode']"
                name="custom-grid-mode"
                :data="table.getRowModel().rows.map((row) => row.original)"
                :handle-row-click="handleRowClick"
            ></slot>

            <div v-else-if="loading" data-testid="data-table-cards-skeleton" class="gap-xs flex flex-col">
                <div v-for="n in 4" :key="n" class="bg-soft h-28 animate-pulse rounded-md"></div>
            </div>

            <slot v-else-if="data.length === 0" name="empty-state">
                <TableEmptyState
                    :icon="emptyStateConfig.icon"
                    :title="emptyStateConfig.title || 'No data available'"
                    :message="emptyStateConfig.message || 'There is no data to display at this time.'"
                />
            </slot>

            <slot v-else-if="table.getRowModel().rows.length === 0" name="no-results">
                <TableEmptyState
                    :icon="noResultsStateConfig.icon || 'i-no-results'"
                    :title="noResultsStateConfig.title || 'No results found'"
                    :message="noResultsStateConfig.message || 'No results found. Try adjusting your search or filters.'"
                />
            </slot>

            <div v-else data-testid="data-table-cards" class="gap-xs flex flex-col">
                <div
                    v-for="(row, index) in table.getRowModel().rows"
                    :key="row.id"
                    class="p-sm bg-primary gap-xs flex flex-col rounded-md"
                    @click="handleRowClick(row.original)"
                >
                    <slot name="mobile-card" :row="row.original" :index="index">
                        <!-- getAllCells, not getVisibleCells: column selection is
                             a table-view concern (the picker only shows there),
                             so cards always present every field. -->
                        <div
                            v-for="rowCell in row.getAllCells()"
                            :key="rowCell.id"
                            class="gap-sm flex items-start justify-between"
                        >
                            <span class="shrink-0 text-(--text-muted)">{{ columnLabel(rowCell) }}</span>
                            <div class="min-w-0 text-right break-words">
                                <CellContent :cell="rowCell" />
                            </div>
                        </div>
                    </slot>
                </div>
            </div>
        </template>

        <!-- overflow-x-auto + min-width:max-content is the mobile table
             pattern from the responsive exploration: columns keep their
             natural width and the row scrolls sideways, instead of columns
             getting squeezed and truncated off-screen. -->
        <div v-else class="w-full overflow-x-auto">
            <table
                :class="tableClass || 'table w-full table-auto'"
                :style="mobileScrollActive ? { minWidth: 'max-content' } : undefined"
            >
                <thead>
                    <tr>
                        <th v-if="enableRowSelection" class="w-12">
                            <input
                                type="checkbox"
                                aria-label="Select all rows on this page"
                                :checked="areAllDisplayedRowsSelected"
                                :indeterminate="areSomeDisplayedRowsSelected"
                                @change="toggleDisplayedRows"
                            />
                        </th>
                        <th
                            v-for="header in table.getFlatHeaders()"
                            :key="header.id"
                            :style="{
                                width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                            }"
                        >
                            <div
                                v-if="header.column.getCanSort()"
                                class="gap-xs flex cursor-pointer items-center select-none"
                                @click="header.column.getToggleSortingHandler()?.($event)"
                            >
                                <span>
                                    {{
                                        typeof header.column.columnDef.header === 'string'
                                            ? header.column.columnDef.header
                                            : header.id
                                    }}
                                </span>
                                <button class="btn-link px-xs">
                                    <i
                                        :class="{
                                            'i-sorting-arrows': !header.column.getIsSorted(),
                                            'i-up-arrow': header.column.getIsSorted() === 'asc',
                                            'i-down-arrow': header.column.getIsSorted() === 'desc',
                                        }"
                                    />
                                </button>
                            </div>
                            <span v-else>
                                {{
                                    typeof header.column.columnDef.header === 'string'
                                        ? header.column.columnDef.header
                                        : header.id
                                }}
                            </span>
                        </th>
                        <th v-if="enableRowExpansion"></th>
                    </tr>
                </thead>

                <tbody>
                    <SkeletonTable v-if="loading" :column-count="visibleColumnCount" :row-count="5" />

                    <template v-else-if="!loading && data.length === 0">
                        <tr class="static-row">
                            <td :colspan="visibleColumnCount">
                                <slot name="empty-state">
                                    <TableEmptyState
                                        :icon="emptyStateConfig.icon"
                                        :title="emptyStateConfig.title || 'No data available'"
                                        :message="
                                            emptyStateConfig.message || 'There is no data to display at this time.'
                                        "
                                    />
                                </slot>
                            </td>
                        </tr>
                    </template>

                    <template v-else-if="!loading && table.getRowModel().rows.length === 0">
                        <tr class="static-row">
                            <td :colspan="visibleColumnCount">
                                <slot name="no-results">
                                    <TableEmptyState
                                        :icon="noResultsStateConfig.icon || 'i-no-results'"
                                        :title="noResultsStateConfig.title || 'No results found'"
                                        :message="
                                            noResultsStateConfig.message ||
                                            'No results found. Try adjusting your search or filters.'
                                        "
                                    />
                                </slot>
                            </td>
                        </tr>
                    </template>

                    <template v-else>
                        <template v-for="row in table.getRowModel().rows" :key="row.id">
                            <tr :class="getRowClass(row.original)" @click="handleRowClick(row.original)">
                                <td v-if="enableRowSelection" class="w-12" @click.stop>
                                    <input
                                        type="checkbox"
                                        :aria-label="`Select row ${getRowKey(row.original, row.id)}`"
                                        :checked="isRowSelected(row.original, row.id)"
                                        @change="toggleRow(row.original, row.id, $event)"
                                    />
                                </td>
                                <!-- In mobile scroll mode the default row
                                     `truncate` inherits nowrap into cells, so
                                     text longer than the .table td max-w cap
                                     paints across the neighbouring column;
                                     wrapping inside the cap keeps it readable. -->
                                <td
                                    v-for="visibleCell in row.getVisibleCells()"
                                    :key="visibleCell.id"
                                    :class="mobileScrollActive ? 'whitespace-normal' : undefined"
                                >
                                    <CellContent :cell="visibleCell" />
                                </td>
                                <td v-if="enableRowExpansion">
                                    <button class="rotate-wrapper flex" @click.stop="() => row.toggleExpanded()">
                                        <i
                                            class="i-arrow-down transition-transform"
                                            :class="{ 'rotate-180': row.getIsExpanded() }"
                                        />
                                    </button>
                                </td>
                            </tr>
                            <Transition v-if="enableRowExpansion" name="expand">
                                <tr v-show="row.getIsExpanded()" class="summary-row mt-sm p-sm">
                                    <td :colspan="visibleColumnCount">
                                        <slot name="expanded-row" :row="row.original" />
                                    </td>
                                </tr>
                            </Transition>
                        </template>
                    </template>
                </tbody>
            </table>
        </div>

        <PaginationButtons
            v-if="totalPages > 1"
            :page="currentPage"
            :total-pages="totalPages"
            @update:page="handlePageChange"
        />
    </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import {
    useVueTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getExpandedRowModel,
    type ColumnDef,
    type SortingState,
    type PaginationState,
    type ExpandedState,
} from '@tanstack/vue-table';
import { ref, computed, watch, isVNode, defineComponent, provide, useSlots, type PropType } from 'vue';
import type { Cell, Row, VisibilityState } from '@tanstack/vue-table';
import SkeletonTable from '@/components/skeleton-table.vue';
import PaginationButtons from '@/components/misc/pagination-buttons.vue';
import { DATA_TABLE_VIEW } from './types';
import type { EmptyStateConfig, MobileMode, NoResultsStateConfig, ToggleableColumn, ViewMode } from './types';
import MobileTableSort from '../support/mobile-table-sort.vue';
import TableEmptyState from './TableEmptyState.vue';
import TableDisplayControls from './TableDisplayControls.vue';
import { useTableViewPrefs } from './useTableViewPrefs';
import { useIsDesktop } from '@/composables/isDesktop';

const props = withDefaults(
    defineProps<{
        data: T[];
        columns: ColumnDef<T, unknown>[];
        loading?: boolean;
        enableSorting?: boolean;
        enableSearch?: boolean;
        enablePagination?: boolean;
        enableRowExpansion?: boolean;
        pageSize?: number;
        searchPlaceholder?: string;
        emptyStateConfig?: EmptyStateConfig;
        noResultsStateConfig?: NoResultsStateConfig;
        tableClass?: string;
        rowClass?: string | ((row: T) => string);
        rowKey?: keyof T | ((row: T) => string | number);
        enableRowSelection?: boolean;
        selectedRowKeys?: Array<string | number>;
        /**
         * Tri-state (ITDEVELOP-3170): leave UNBOUND and DataTable manages the
         * display — the user's view toggle switches between the table and the
         * cards (the page's custom-grid-mode slot when provided, generic
         * cards otherwise). Bind a boolean and the PAGE manages the display —
         * true forces the custom-grid slot, false forces the table, and the
         * user-facing display controls are hidden.
         */
        gridMode?: boolean;
        /**
         * How the table renders below the desktop breakpoint (1024px):
         * - 'auto' (default): stays a table, keeping natural column widths and
         *   scrolling sideways (ITDEVELOP-3146); the user can switch to cards.
         * - 'cards': cards by default — stacked label/value cards, or the
         *   custom-grid-mode slot when the page provides one — for pages whose
         *   bespoke card design beats a table on a phone; the user can switch
         *   back to the table.
         * - 'table': no mobile adaptation at all — the squeezed desktop table,
         *   without the sideways scroll (legacy escape hatch).
         * `gridMode` (the custom-grid slot) always wins over this.
         */
        mobileMode?: MobileMode;
        /**
         * ITDEVELOP-3170: the user-facing card/table toggle + column picker.
         * On by default for every table; hidden automatically for pages that
         * bring their own custom-grid-mode slot (they control their own
         * display). Pass false to opt a table out entirely.
         */
        enableDisplayControls?: boolean;
        /**
         * localStorage key scoping the persisted view/column choices. Defaults
         * to the page's path, so each page's tables remember independently;
         * override when one page hosts several tables.
         */
        prefsKey?: string;
        title?: string;
        defaultSortColumn?: string;
        defaultSortDirection?: 'asc' | 'desc';
    }>(),
    {
        loading: false,
        // Explicit undefined default: a Boolean prop would otherwise cast
        // "not passed" to false, erasing the unbound = DataTable-managed state.
        gridMode: undefined,
        mobileMode: 'auto',
        enableDisplayControls: true,
        prefsKey: undefined,
        enableSorting: false,
        enableSearch: false,
        enablePagination: false,
        enableRowExpansion: false,
        enableRowSelection: false,
        selectedRowKeys: () => [],
        pageSize: 25,
        searchPlaceholder: 'Search',
        emptyStateConfig: () => ({}),
        noResultsStateConfig: () => ({}),
        defaultSortDirection: 'asc',
    },
);

const emit = defineEmits<{
    'row-click': [row: T];
    'sort-change': [column: string, direction: 'asc' | 'desc' | false];
    'page-change': [page: number];
    'update:selectedRowKeys': [keys: Array<string | number>];
}>();

const searchQuery = ref('');
const isDesktop = useIsDesktop();
const slots = useSlots();

// ITDEVELOP-3170: user-facing display controls, on for every table unless the
// page opted out or manages the display itself by binding gridMode (e.g. the
// admin organizations list with its own grid/list toggle, or embedded compact
// lists like the dashboard's).
const displayControlsActive = computed(() => props.enableDisplayControls && props.gridMode === undefined);

const {
    view,
    hiddenColumns,
    toggleColumn,
    reset: resetViewPrefs,
} = useTableViewPrefs(props.prefsKey ?? window.location.pathname);

function columnDefId(col: ColumnDef<T, unknown>): string {
    if ('accessorKey' in col && col.accessorKey) return String(col.accessorKey);
    return String(col.id ?? '');
}

function setView(next: ViewMode) {
    view.value = next;
}

const toggleableColumns = computed<ToggleableColumn[]>(() =>
    props.columns.map((col, index) => {
        const id = columnDefId(col);
        return {
            id,
            label: typeof col.header === 'string' && col.header ? col.header : id,
            visible: !hiddenColumns.value.includes(id),
            // The first column is the row's identity and 'actions' its
            // operations — hiding either leaves rows unusable.
            locked: index === 0 || id === 'actions',
        };
    }),
);

// TanStack column visibility, driven by the persisted hidden list. Hidden
// columns disappear from headers, body cells AND the card view (which renders
// getVisibleCells), while sorting state on them stays valid.
const columnVisibility = computed<VisibilityState>(() =>
    Object.fromEntries(hiddenColumns.value.map((id) => [id, false])),
);

// ITDEVELOP-3146/3170 rendering choice: explicit gridMode (custom slot) wins
// in the template; then the user's persisted choice; with no choice yet,
// mobileMode's developer default applies (cards below the breakpoint only when
// a page sets mobileMode="cards"). mobileScrollActive keeps the slim
// scrollable table below the breakpoint.
const effectiveView = computed<ViewMode>(() => {
    // Desktop is table-only (ITDEVELOP-3170 QA): cards exist to make wide
    // tables usable on narrow screens, so a 'cards' choice made on a phone
    // does not follow the user to a desktop viewport.
    if (isDesktop.value) {
        return 'table';
    }
    if (displayControlsActive.value && view.value) {
        return view.value;
    }
    // Mobile stays a table by default — natural column widths with sideways
    // scroll, the pattern ITDEVELOP-3146 implemented from the responsive
    // exploration (Figma "Portal" node 10444-300288). Cards are the user's
    // choice via the toggle, or a page's opt-in via mobileMode="cards".
    return props.mobileMode === 'cards' ? 'cards' : 'table';
});
const cardsActive = computed(() => effectiveView.value === 'cards');
// Cells adapt to the view they render in (see DATA_TABLE_VIEW in types.ts).
provide(DATA_TABLE_VIEW, effectiveView);
// When the user picks table view on mobile, the table keeps its natural
// column widths and scrolls sideways — unless the page opted out of mobile
// adaptation entirely with mobileMode="table".
const mobileScrollActive = computed(() => !isDesktop.value && props.mobileMode !== 'table');

function columnLabel(cell: Cell<T, unknown>): string {
    const header = cell.column.columnDef.header;
    return typeof header === 'string' ? header : cell.column.id;
}

// Initialize sorting with default or first column
const getInitialSorting = (): SortingState => {
    if (props.defaultSortColumn) {
        return [{ id: props.defaultSortColumn, desc: props.defaultSortDirection === 'desc' }];
    }
    // Default to first column ascending
    if (props.columns.length > 0) {
        const firstColumn = props.columns[0];
        const columnId =
            'accessorKey' in firstColumn && firstColumn.accessorKey
                ? String(firstColumn.accessorKey)
                : 'id' in firstColumn && firstColumn.id
                  ? firstColumn.id
                  : null;

        if (columnId) {
            return [{ id: columnId, desc: false }];
        }
    }
    return [];
};

const sorting = ref<SortingState>(getInitialSorting());
const selectedKey = computed(() => `${sorting.value[0]?.id}-${sorting.value[0]?.desc ? 'descending' : 'ascending'}`);
const pagination = ref<PaginationState>({
    pageIndex: 0,
    pageSize: props.enablePagination ? props.pageSize : 999,
});
const expanded = ref<ExpandedState>({});

// Global filter function for search
const globalFilterFn = (row: Row<T>, columnId: string, filterValue: string) => {
    const searchStr = filterValue.toLowerCase();
    // Search across all row values
    return Object.values(row.original).some((value) =>
        String(value ?? '')
            .toLowerCase()
            .includes(searchStr),
    );
};

const table = useVueTable({
    get data() {
        return props.data;
    },
    get columns() {
        return props.columns;
    },
    state: {
        get sorting() {
            return sorting.value;
        },
        get globalFilter() {
            return searchQuery.value;
        },
        get pagination() {
            return pagination.value;
        },
        get expanded() {
            return expanded.value;
        },
        get columnVisibility() {
            return columnVisibility.value;
        },
    },
    onSortingChange: (updaterOrValue) => {
        sorting.value = typeof updaterOrValue === 'function' ? updaterOrValue(sorting.value) : updaterOrValue;
    },
    onPaginationChange: (updaterOrValue) => {
        pagination.value = typeof updaterOrValue === 'function' ? updaterOrValue(pagination.value) : updaterOrValue;
    },
    onExpandedChange: (updaterOrValue) => {
        expanded.value = typeof updaterOrValue === 'function' ? updaterOrValue(expanded.value) : updaterOrValue;
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: globalFilterFn,
    enableSorting: props.enableSorting,
    enableSortingRemoval: false,
    enableExpanding: props.enableRowExpansion,
    manualPagination: false,
});

const currentPage = computed(() => pagination.value.pageIndex + 1);
const totalPages = computed(() => table.getPageCount());
const visibleColumnCount = computed(
    () => props.columns.length + Number(props.enableRowSelection) + Number(props.enableRowExpansion),
);
const selectedRowKeySet = computed(() => new Set(props.selectedRowKeys));
const displayedRowKeys = computed(() => table.getRowModel().rows.map((row) => getRowKey(row.original, row.id)));
const areAllDisplayedRowsSelected = computed(
    () =>
        displayedRowKeys.value.length > 0 &&
        displayedRowKeys.value.every((key) => selectedRowKeySet.value.has(key)),
);
const areSomeDisplayedRowsSelected = computed(
    () =>
        !areAllDisplayedRowsSelected.value &&
        displayedRowKeys.value.some((key) => selectedRowKeySet.value.has(key)),
);

watch(sorting, (newSorting) => {
    if (newSorting.length > 0) {
        const sort = newSorting[0];
        emit('sort-change', sort.id, sort.desc ? 'desc' : 'asc');
    }
});

watch(
    () => pagination.value.pageIndex,
    (newPageIndex) => {
        emit('page-change', newPageIndex + 1);
    },
);

watch(
    () => props.pageSize,
    (newPageSize) => {
        if (props.enablePagination) {
            if (pagination.value.pageSize !== newPageSize) {
                pagination.value.pageSize = newPageSize;
            }
        }
    },
);

function handlePageChange(page: number) {
    table.setPageIndex(page - 1);
}

function handleRowClick(row: T) {
    emit('row-click', row);
}

function getRowKey(row: T, fallbackKey: string): string | number {
    if (typeof props.rowKey === 'function') {
        return props.rowKey(row);
    }
    if (props.rowKey !== undefined) {
        return row[props.rowKey] as string | number;
    }
    return fallbackKey;
}

function isRowSelected(row: T, fallbackKey: string): boolean {
    return selectedRowKeySet.value.has(getRowKey(row, fallbackKey));
}

function toggleRow(row: T, fallbackKey: string, event: Event) {
    const key = getRowKey(row, fallbackKey);
    const selectedKeys = new Set(props.selectedRowKeys);
    if ((event.target as HTMLInputElement).checked) {
        selectedKeys.add(key);
    } else {
        selectedKeys.delete(key);
    }
    emit('update:selectedRowKeys', [...selectedKeys]);
}

function toggleDisplayedRows(event: Event) {
    const selectedKeys = new Set(props.selectedRowKeys);
    if ((event.target as HTMLInputElement).checked) {
        displayedRowKeys.value.forEach((key) => selectedKeys.add(key));
    } else {
        displayedRowKeys.value.forEach((key) => selectedKeys.delete(key));
    }
    emit('update:selectedRowKeys', [...selectedKeys]);
}

function getRowClass(row: T): string {
    if (typeof props.rowClass === 'function') {
        return props.rowClass(row);
    }
    return props.rowClass || 'truncate';
}

function setSorting(column: string, direction?: 'ascending' | 'descending') {
    sorting.value = [{ id: column, desc: direction === 'descending' }];
    // if (direction === 'ascending' || direction === 'descending') {
    //     sortColumn.value = column as keyof User; // hack, make sortTable column be keyofUser
    //     // sortColumn.value = column;
    //     sortAscending.value = direction === 'ascending' ? true : false;
    // } else {
    //     if (sortColumn.value === column) {
    //         sortAscending.value = !sortAscending.value;
    //     } else {
    //         sortColumn.value = column as keyof User;
    //         // sortColumn.value = column;
    //         sortAscending.value = true;
    //     }
    // }

    // console.table(sortedUsers.value.map((user) => [user.ID, user.FIRST_NAME]));
}

function clearSorting() {
    sorting.value = [];
}

// Component to safely render cell content without double-calling render functions
const CellContent = defineComponent({
    props: { cell: { type: Object as PropType<Cell<T, unknown>>, required: true } },
    setup(props) {
        return () => {
            const cell = props.cell;
            const cellDef = cell.column.columnDef.cell;

            if (typeof cellDef === 'function') {
                const result = cellDef(cell.getContext());
                if (isVNode(result)) {
                    return result;
                }
                return result;
            }

            return cell.renderValue() ?? cell.getValue() ?? '';
        };
    },
});
</script>

<style scoped>
.data-table-wrapper {
    position: relative;
    width: 100%;
}

/* Expand transition animation */
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.summary-row {
    background-color: var(--bg-lightest);
}

.summary-row td {
    padding: 1rem;
}
</style>
